import React, { useState, useEffect } from 'react';
import { X, Bell, Mail, Smartphone, Wifi, Check } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useToast } from '../contexts/ToastContext';
import { BACKEND_CONFIG } from '../config/backendConfig';
import { useResponsive } from '../hooks/useResponsive';

interface NotificationPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NotificationPreferences {
  email_enabled: boolean;
  push_enabled: boolean;
  socket_enabled: boolean;
  preferences: {
    email: {
      weekly_school_contact_reminder: boolean;
      monthly_recruiting_calendar: boolean;
      tournament_sharing_reminder: boolean;
      inactivity_reengagement: boolean;
      coach_note: boolean;
      social_connection_nudge: boolean;
      freemium_upgrade_nudge: boolean;
      profile_completion_nudge: boolean;
      game_plan_nudge?: boolean;
      coach_engaged: boolean;
      follow_up_reminder: boolean;
      promo_expiring: boolean;
    };
    push: {
      tournament_reminder: boolean;
      calendar_event_reminder: boolean;
      weekly_tournament_schedule_reminder: boolean;
      school_milestone: boolean;
      coach_tweet: boolean;
      camp_nearby: boolean;
      coach_note: boolean;
      social_connection_nudge: boolean;
      freemium_upgrade_nudge: boolean;
      profile_completion_nudge: boolean;
      game_plan_nudge?: boolean;
      coach_engaged: boolean;
      follow_up_reminder: boolean;
      promo_expiring: boolean;
      coach_profile_viewed: boolean;
    };
    socket: {
      school_added: boolean;
      school_changed: boolean;
      tournament_added: boolean;
      coach_tweet: boolean;
      profile_viewed: boolean;
      coach_note: boolean;
      coach_profile_viewed: boolean;
    };
  };
}

const NOTIFICATION_DESCRIPTIONS = {
  email: {
    weekly_school_contact_reminder: {
      title: 'Weekly School Contact Reminder',
      description: 'Reminder to reach out to coaches on your school list (Sundays 6PM EST)'
    },
    monthly_recruiting_calendar: {
      title: 'Monthly Recruiting Calendar',
      description: 'Monthly summary of upcoming recruiting events and deadlines (1st of month 9AM EST)'
    },
    tournament_sharing_reminder: {
      title: 'Tournament Sharing Reminder',
      description: 'Reminder to share tournament schedules with coaches (Daily 10AM EST, 14 days before)'
    },
    inactivity_reengagement: {
      title: 'Activity Reminders',
      description: 'Gentle nudges to keep your recruiting profile active (After 14 days inactive)'
    },
    coach_note: {
      title: 'Coach Notes',
      description: 'Get emailed when your coach or staff adds a note to your profile'
    },
    social_connection_nudge: {
      title: 'Connect Accounts Reminders',
      description: 'Reminders to connect Gmail, X, and YouTube to your recruiting profile'
    },
    freemium_upgrade_nudge: {
      title: 'Upgrade Reminders',
      description: 'Reminders about upgrading to contact every program on your list.'
    },
    game_plan_nudge: {
      title: 'Game Plan Reminders',
      description: 'Reminders for the next step in your game plan, in your first week.'
    },
    profile_completion_nudge: {
      title: 'Complete Profile Reminders',
      description: 'Reminders to fill in remaining items on your recruiting profile (24 hours, 3 days, and 7 days after qualifying)'
    },
    coach_engaged: {
      title: 'A Coach Is Interested',
      description: 'Get emailed the moment a college coach opens one of your emails'
    },
    follow_up_reminder: {
      title: 'Follow-up Reminders',
      description: 'A nudge to reply when a coach has engaged but you haven\'t responded yet'
    },
    promo_expiring: {
      title: 'Promo Ending',
      description: 'A heads-up a few days before and on the day a promotional free period ends and your plan returns to free'
    },
    coach_note_reply: {
      title: 'Replies to Your Notes',
      description: 'Get emailed when a player or parent replies to a note you wrote'
    }
  },
  push: {
    tournament_reminder: {
      title: 'Tournament Reminders',
      description: 'Push notification for upcoming tournaments (Daily 9AM EST, 1 day before)'
    },
    calendar_event_reminder: {
      title: 'Calendar Event Reminders',
      description: 'Reminders for events on your recruiting calendar (Daily 8AM EST, 3 days before)'
    },
    weekly_tournament_schedule_reminder: {
      title: 'Weekly Tournament Schedule',
      description: 'Weekly overview of your upcoming tournament schedule (Mondays 8AM EST)'
    },
    school_milestone: {
      title: 'School Milestones',
      description: 'Celebrate achievements like adding your 10th school to your list'
    },
    coach_tweet: {
      title: 'Coach Tweets',
      description: 'Get notified when coaches from schools on your list post on X'
    },
    camp_nearby: {
      title: 'Camps Near You',
      description: 'Get notified when college camps are happening within 100 miles of your location (Daily 9AM EST)'
    },
    coach_note: {
      title: 'Coach Notes',
      description: 'Push notification when your coach or staff adds a note to your profile'
    },
    social_connection_nudge: {
      title: 'Connect Accounts Reminders',
      description: 'Reminders to connect Gmail, X, and YouTube to your recruiting profile'
    },
    freemium_upgrade_nudge: {
      title: 'Upgrade Reminders',
      description: 'Reminders about upgrading to contact every program on your list.'
    },
    game_plan_nudge: {
      title: 'Game Plan Reminders',
      description: 'Reminders for the next step in your game plan, in your first week.'
    },
    profile_completion_nudge: {
      title: 'Complete Profile Reminders',
      description: 'Reminders to fill in remaining items on your recruiting profile (24 hours, 3 days, and 7 days after qualifying)'
    },
    coach_engaged: {
      title: 'A Coach Is Interested',
      description: 'Get a push the moment a college coach opens one of your emails'
    },
    follow_up_reminder: {
      title: 'Follow-up Reminders',
      description: 'A nudge to reply when a coach has engaged but you haven\'t responded yet'
    },
    promo_expiring: {
      title: 'Promo Ending',
      description: 'A heads-up a few days before and on the day a promotional free period ends and your plan returns to free'
    },
    coach_profile_viewed: {
      title: 'College Coach Profile Views',
      description: 'Get notified when a college coach opens your profile, with their school and their name unless they hide it (at most once a day per coaching staff)'
    },
    coach_attending_tournament: {
      title: 'A College Coach Is Going to Your Tournament',
      description: 'Get notified when a college coach plans to attend a tournament you are going to'
    },
    coach_note_reply: {
      title: 'Replies to Your Notes',
      description: 'Get a push when a player or parent replies to a note you wrote'
    }
  },
  socket: {
    school_added: {
      title: 'School Added',
      description: 'Real-time notifications when you add a school to your list'
    },
    school_changed: {
      title: 'School Status Changed',
      description: 'Instant updates when a school\'s interest level changes'
    },
    tournament_added: {
      title: 'Tournament Added',
      description: 'Real-time notifications for new tournaments'
    },
    coach_tweet: {
      title: 'Coach Tweets',
      description: 'Real-time alerts when coaches from your schools tweet'
    },
    profile_viewed: {
      title: 'Profile Views',
      description: 'Get notified when someone views your public recruiting profile'
    },
    coach_note: {
      title: 'Coach Notes',
      description: 'Real-time notification when your coach or staff adds a note to your profile'
    },
    coach_profile_viewed: {
      title: 'College Coach Profile Views',
      description: 'Real-time alert when a college coach opens your profile, with their school and their name unless they hide it'
    },
    coach_attending_tournament: {
      title: 'A College Coach Is Going to Your Tournament',
      description: 'Real-time alert when a college coach plans to attend a tournament you are going to'
    },
    coach_note_reply: {
      title: 'Replies to Your Notes',
      description: 'Real-time alert when a player or parent replies to a note you wrote'
    }
  }
};

// Types only a college coach receives (the new-player-matches digest). The
// preferences endpoint returns them for every account, but a family can never
// get one, so a switch for it here would mean nothing.
const COACH_ONLY_TYPES = new Set(['coach_player_match']);

// Types only team staff receive (APS-253: a player or parent replied to your
// note). Shown to a family account only when it also holds a staff role.
const STAFF_ONLY_TYPES = new Set(['coach_note_reply']);

const familyEntries = (channelPrefs: object, showStaffTypes = false): Array<[string, boolean]> =>
  (Object.entries(channelPrefs) as Array<[string, boolean]>).filter(
    ([key]) => !COACH_ONLY_TYPES.has(key) && (showStaffTypes || !STAFF_ONLY_TYPES.has(key))
  );

// Turn an unknown backend preference key (e.g. "new_backend_nudge") into a
// readable title so a key we don't have copy for never crashes the modal.
const humanizeKey = (key: string): string =>
  key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Safe accessor: the backend can add a preference key before the frontend
// ships copy for it. Indexing NOTIFICATION_DESCRIPTIONS[channel][key] directly
// then throws and white-screens the whole modal — fall back to a humanized key.
const getNotificationMeta = (
  channel: 'email' | 'push' | 'socket',
  key: string
): { title: string; description: string } => {
  const channelMap = NOTIFICATION_DESCRIPTIONS[channel] as Record<
    string,
    { title: string; description: string }
  >;
  const meta = channelMap?.[key];
  if (meta) return meta;
  return { title: humanizeKey(key), description: '' };
};

const NotificationPreferencesModal: React.FC<NotificationPreferencesModalProps> = ({ isOpen, onClose }) => {
  const { sessionToken, user } = useAuth();
  const { can } = usePermissions();
  const showStaffTypes = user?.user_type === 'staff' || !!user?.organization_id || can('*:*');
  const toast = useToast();
  const { isMobile } = useResponsive();
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchPreferences();
    }
  }, [isOpen]);

  const fetchPreferences = async () => {
    if (!sessionToken) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_CONFIG.baseURL}/notification-preferences`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPreferences(data.data);
      } else {
        toast.error('Failed to load notification preferences');
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast.error('Failed to load notification preferences');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleChannel = async (channel: 'email' | 'push' | 'socket') => {
    if (!sessionToken || !preferences) return;

    const newValue = !preferences[`${channel}_enabled`];
    
    // Optimistic update
    setPreferences(prev => prev ? { ...prev, [`${channel}_enabled`]: newValue } : prev);

    try {
      const response = await fetch(`${BACKEND_CONFIG.baseURL}/notification-preferences/channel`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ channel, enabled: newValue })
      });

      if (!response.ok) {
        // Revert on error
        setPreferences(prev => prev ? { ...prev, [`${channel}_enabled`]: !newValue } : prev);
        toast.error('Failed to update preference');
      }
    } catch (error) {
      // Revert on error
      setPreferences(prev => prev ? { ...prev, [`${channel}_enabled`]: !newValue } : prev);
      toast.error('Failed to update preference');
    }
  };

  const toggleIndividualNotification = async (channel: 'email' | 'push' | 'socket', type: string) => {
    if (!sessionToken || !preferences) return;

    const currentValue = (preferences.preferences[channel] as any)[type];
    const newValue = !currentValue;

    // Optimistic update
    setPreferences(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        preferences: {
          ...prev.preferences,
          [channel]: {
            ...prev.preferences[channel],
            [type]: newValue
          }
        }
      };
    });

    try {
      const response = await fetch(`${BACKEND_CONFIG.baseURL}/notification-preferences/individual`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ channel, type, enabled: newValue })
      });

      if (!response.ok) {
        // Revert on error
        setPreferences(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            preferences: {
              ...prev.preferences,
              [channel]: {
                ...prev.preferences[channel],
                [type]: currentValue
              }
            }
          };
        });
        toast.error('Failed to update preference');
      }
    } catch (error) {
      // Revert on error
      setPreferences(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          preferences: {
            ...prev.preferences,
            [channel]: {
              ...prev.preferences[channel],
              [type]: currentValue
            }
          }
        };
      });
      toast.error('Failed to update preference');
    }
  };

  if (!isOpen) return null;

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email': return <Mail size={18} />;
      case 'push': return <Smartphone size={18} />;
      case 'socket': return <Wifi size={18} />;
      default: return <Bell size={18} />;
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          fontFamily: 'Manrope, sans-serif',
        }}
      >
        {/* Modal */}
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            width: isMobile ? 'calc(100vw - 32px)' : '90%',
            maxWidth: isMobile ? 'calc(100vw - 32px)' : '700px',
            maxHeight: isMobile ? 'calc(100vh - 100px)' : '85vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: isMobile ? '16px' : '24px 28px',
              borderBottom: '1px solid #E5E7EB',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: '#151133',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Bell size={20} color="white" />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' }}>
                  Notification Preferences
                </h2>
                <p style={{ margin: '2px 0 0', fontSize: '14px', color: '#6B7280' }}>
                  Manage how you receive updates
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                border: 'none',
                background: '#F3F4F6',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#E5E7EB')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#F3F4F6')}
            >
              <X size={18} color="#6B7280" />
            </button>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '14px', color: '#6B7280' }}>Loading preferences...</div>
              </div>
            ) : preferences ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Email Notifications */}
                <div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '16px',
                      padding: '16px',
                      background: preferences.email_enabled ? '#F0F9FF' : '#F9FAFB',
                      border: `1px solid ${preferences.email_enabled ? '#BFDBFE' : '#E5E7EB'}`,
                      borderRadius: '12px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '8px',
                          background: preferences.email_enabled ? '#151133' : '#9CA3AF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Mail size={18} color="white" />
                      </div>
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                          Email Notifications
                        </div>
                        <div style={{ fontSize: '13px', color: '#6B7280' }}>
                          {preferences.email_enabled ? 'Enabled' : 'Disabled'}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={preferences.email_enabled}
                      aria-label="Email notifications"
                      onClick={() => toggleChannel('email')}
                      style={{
                        position: 'relative',
                        width: '48px',
                        height: '28px',
                        borderRadius: '14px',
                        border: 'none',
                        background: preferences.email_enabled ? '#151133' : '#D1D5DB',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: '3px',
                          left: preferences.email_enabled ? '23px' : '3px',
                          width: '22px',
                          height: '22px',
                          borderRadius: '50%',
                          background: 'white',
                          transition: 'all 0.2s',
                        }}
                      />
                    </button>
                  </div>

                  {preferences.email_enabled && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '16px' }}>
                      {familyEntries(preferences.preferences.email, showStaffTypes).map(([key, value]) => (
                        <div
                          key={key}
                          role="switch"
                          aria-checked={!!value}
                          aria-label={getNotificationMeta('email', key).title}
                          tabIndex={0}
                          onClick={() => toggleIndividualNotification('email', key)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleIndividualNotification('email', key);
                            }
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 16px',
                            background: 'white',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#F9FAFB')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '2px' }}>
                              {getNotificationMeta('email', key).title}
                            </div>
                            {getNotificationMeta('email', key).description && (
                              <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                {getNotificationMeta('email', key).description}
                              </div>
                            )}
                          </div>
                          <div
                            style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '4px',
                              border: `2px solid ${value ? '#151133' : '#D1D5DB'}`,
                              background: value ? '#151133' : 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {value && <Check size={14} color="white" />}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Push Notifications */}
                <div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '16px',
                      padding: '16px',
                      background: preferences.push_enabled ? '#F0FDF4' : '#F9FAFB',
                      border: `1px solid ${preferences.push_enabled ? '#BBF7D0' : '#E5E7EB'}`,
                      borderRadius: '12px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '8px',
                          background: preferences.push_enabled ? '#12B76A' : '#9CA3AF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Smartphone size={18} color="white" />
                      </div>
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                          Push Notifications
                        </div>
                        <div style={{ fontSize: '13px', color: '#6B7280' }}>
                          {preferences.push_enabled ? 'Enabled' : 'Disabled'}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={preferences.push_enabled}
                      aria-label="Push notifications"
                      onClick={() => toggleChannel('push')}
                      style={{
                        position: 'relative',
                        width: '48px',
                        height: '28px',
                        borderRadius: '14px',
                        border: 'none',
                        background: preferences.push_enabled ? '#12B76A' : '#D1D5DB',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: '3px',
                          left: preferences.push_enabled ? '23px' : '3px',
                          width: '22px',
                          height: '22px',
                          borderRadius: '50%',
                          background: 'white',
                          transition: 'all 0.2s',
                        }}
                      />
                    </button>
                  </div>

                  {preferences.push_enabled && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '16px' }}>
                      {familyEntries(preferences.preferences.push, showStaffTypes).map(([key, value]) => (
                        <div
                          key={key}
                          role="switch"
                          aria-checked={!!value}
                          aria-label={getNotificationMeta('push', key).title}
                          tabIndex={0}
                          onClick={() => toggleIndividualNotification('push', key)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleIndividualNotification('push', key);
                            }
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 16px',
                            background: 'white',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#F9FAFB')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '2px' }}>
                              {getNotificationMeta('push', key).title}
                            </div>
                            {getNotificationMeta('push', key).description && (
                              <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                {getNotificationMeta('push', key).description}
                              </div>
                            )}
                          </div>
                          <div
                            style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '4px',
                              border: `2px solid ${value ? '#12B76A' : '#D1D5DB'}`,
                              background: value ? '#12B76A' : 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {value && <Check size={14} color="white" />}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Real-time Notifications */}
                <div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '16px',
                      padding: '16px',
                      background: preferences.socket_enabled ? '#FEF3C7' : '#F9FAFB',
                      border: `1px solid ${preferences.socket_enabled ? '#FDE68A' : '#E5E7EB'}`,
                      borderRadius: '12px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '8px',
                          background: preferences.socket_enabled ? '#F59E0B' : '#9CA3AF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Wifi size={18} color="white" />
                      </div>
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                          Real-time Notifications
                        </div>
                        <div style={{ fontSize: '13px', color: '#6B7280' }}>
                          {preferences.socket_enabled ? 'Enabled' : 'Disabled'}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={preferences.socket_enabled}
                      aria-label="In-app notifications"
                      onClick={() => toggleChannel('socket')}
                      style={{
                        position: 'relative',
                        width: '48px',
                        height: '28px',
                        borderRadius: '14px',
                        border: 'none',
                        background: preferences.socket_enabled ? '#F59E0B' : '#D1D5DB',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: '3px',
                          left: preferences.socket_enabled ? '23px' : '3px',
                          width: '22px',
                          height: '22px',
                          borderRadius: '50%',
                          background: 'white',
                          transition: 'all 0.2s',
                        }}
                      />
                    </button>
                  </div>

                  {preferences.socket_enabled && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '16px' }}>
                      {familyEntries(preferences.preferences.socket, showStaffTypes).map(([key, value]) => (
                        <div
                          key={key}
                          role="switch"
                          aria-checked={!!value}
                          aria-label={getNotificationMeta('socket', key).title}
                          tabIndex={0}
                          onClick={() => toggleIndividualNotification('socket', key)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleIndividualNotification('socket', key);
                            }
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 16px',
                            background: 'white',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#F9FAFB')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '2px' }}>
                              {getNotificationMeta('socket', key).title}
                            </div>
                            {getNotificationMeta('socket', key).description && (
                              <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                {getNotificationMeta('socket', key).description}
                              </div>
                            )}
                          </div>
                          <div
                            style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '4px',
                              border: `2px solid ${value ? '#F59E0B' : '#D1D5DB'}`,
                              background: value ? '#F59E0B' : 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {value && <Check size={14} color="white" />}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                <div style={{ marginBottom: '14px' }}>Couldn't load your preferences.</div>
                <button
                  type="button"
                  onClick={() => fetchPreferences()}
                  style={{ padding: '9px 18px', background: '#F9550D', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Manrope, sans-serif' }}
                >
                  Try again
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default NotificationPreferencesModal;
